import React, { useEffect, useState } from 'react';
import { Download, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/lib/axios';

const eur = (value) => new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
}).format(Number(value || 0));

const periodLabel = (period) => {
  const [year, month] = String(period || '').split('-').map(Number);
  if (!year || !month) return period || '—';
  return new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' })
    .format(new Date(year, month - 1, 1));
};

export default function TeacherPayrollPage() {
  const [statements, setStatements] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    api.get('/teacher/payroll/statements')
      .then((response) => setStatements(response.data || []))
      .catch((error) => toast.error(
        error.response?.data?.detail || 'Honorarabrechnungen konnten nicht geladen werden',
      ))
      .finally(() => setLoading(false));
  }, []);

  const showDetails = async (statement) => {
    setDetailLoading(true);
    try {
      const response = await api.get(`/teacher/payroll/statements/${statement.run_id}`);
      setSelected(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Abrechnung konnte nicht geladen werden');
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadPdf = async (statement) => {
    setDownloading(statement.run_id);
    try {
      const response = await api.get(
        `/teacher/payroll/statements/${statement.run_id}/pdf`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `honorarabrechnung_${statement.period}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'PDF konnte nicht geladen werden');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Layout>
      <main className="p-6 md:p-8 space-y-6 max-w-4xl" data-testid="teacher-payroll-page">
        <header>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="h-6 w-6 text-brand-red" />
            Meine Honorarabrechnungen
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Hier stehen Ihre von der Verwaltung fertiggestellten Abrechnungen zum Abruf bereit.
          </p>
        </header>

        {loading ? (
          <div className="py-12 text-center">
            <Loader2 className="h-6 w-6 animate-spin inline text-slate-400" aria-label="Wird geladen" />
          </div>
        ) : statements.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center text-sm text-slate-500">
              Es liegt noch keine fertiggestellte Honorarabrechnung vor.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {statements.map((statement) => (
              <Card key={statement.run_id} className="border-slate-200">
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
                  <button
                    type="button"
                    onClick={() => showDetails(statement)}
                    className="min-h-11 flex-1 text-left rounded focus:outline-none focus:ring-2 focus:ring-brand-red/40"
                    data-testid={`teacher-payroll-open-${statement.run_id}`}
                  >
                    <span className="block font-semibold text-slate-900 capitalize">
                      {periodLabel(statement.period)}
                    </span>
                    <span className="block text-sm text-slate-500">Fertiggestellt</span>
                  </button>
                  <span className="font-semibold tabular-nums text-slate-900 sm:min-w-28 sm:text-right">
                    {eur(statement.total)}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => downloadPdf(statement)}
                    disabled={downloading === statement.run_id}
                    className="min-h-11"
                    data-testid={`teacher-payroll-pdf-${statement.run_id}`}
                  >
                    {downloading === statement.run_id
                      ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      : <Download className="h-4 w-4 mr-2" />}
                    PDF
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {(detailLoading || selected) && (
          <Card className="border-slate-200" data-testid="teacher-payroll-detail">
            <CardContent className="p-5">
              {detailLoading ? (
                <div className="py-8 text-center">
                  <Loader2 className="h-5 w-5 animate-spin inline text-slate-400" aria-label="Wird geladen" />
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-4 mb-4">
                    <h2 className="font-semibold text-slate-900 capitalize">
                      Abrechnung {periodLabel(selected.period)}
                    </h2>
                    <span className="font-bold tabular-nums text-slate-900">{eur(selected.total)}</span>
                  </div>
                  <ul className="divide-y divide-slate-100">
                    {(selected.grouped_items || []).map((item, index) => (
                      <li
                        key={`${item.veranstaltung}-${index}`}
                        className="py-3 flex items-start justify-between gap-4"
                      >
                        <div>
                          <p className="text-sm font-medium text-slate-800">{item.veranstaltung}</p>
                          {item.component_labels?.length > 0 && (
                            <p className="text-xs text-slate-500 mt-0.5">
                              {item.component_labels.join(', ')}
                            </p>
                          )}
                        </div>
                        <span className="text-sm font-semibold tabular-nums text-slate-800">
                          {eur(item.gesamt)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </Layout>
  );
}
